import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Section, SectionTitle, PageHero } from '@/components/shared/Section';
import { EnterpriseForm } from '@/components/shared/Forms';
import { ShieldCheck, Phone, Mail, MapPin, MessageCircle, Clock, Sparkles } from 'lucide-react';

// Topics conocidos → label, mensaje pre-llenado y badge
const TOPIC_CONFIG = {
  'avx': {
    label: 'AVX · Visual Experience',
    message: 'Hola, vengo desde la página de AVX (ALLCOM Visual Experience) y me gustaría conocer más detalles sobre su portafolio LED.',
    badge: 'Solicitud AVX',
  },
  'avx-demo-virtual': {
    label: 'AVX · Demo Virtual',
    message: 'Hola, me gustaría agendar una Demo Virtual de AVX para conocer el showroom digital 360°.',
    badge: 'Demo Virtual AVX',
  },
  'avx-visita-tecnica': {
    label: 'AVX · Visita Técnica',
    message: 'Hola, necesito coordinar una Visita Técnica AVX para validar la factibilidad de un proyecto en mi ubicación.',
    badge: 'Visita Técnica AVX',
  },
  'avx-consultor-avx': {
    label: 'AVX · Reunión con Consultor',
    message: 'Hola, quiero agendar una reunión con un Consultor AVX para evaluar un proyecto de integración a gran escala.',
    badge: 'Consultor AVX',
  },
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const topic = (searchParams.get('topic') || '').trim().toLowerCase();
  const topicConfig = TOPIC_CONFIG[topic] || null;
  const sourcePage = topic ? `/contacto?topic=${topic}` : '/contacto';

  return (
    <div data-testid="contact-page">
      <PageHero
        badge="CONTACTO"
        badgeIcon={ShieldCheck}
        h1={topicConfig ? `Solicitud ${topicConfig.badge}` : 'Conversemos sobre tu proyecto'}
        subtitle={topicConfig
          ? 'Estás un paso de hablar con un especialista AVX. Completa tus datos y te respondemos en menos de 24 horas hábiles.'
          : 'Completa el formulario y un especialista se contactará contigo en menos de 24 horas. Sin compromiso.'}
        dark={false}
        primaryCTA={
          <a
            href="https://wa.me/56975294340?text=Hola,%20necesito%20cotizar"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white bg-emerald-500 rounded-xl hover:bg-emerald-600 shadow-lg"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp directo
          </a>
        }
      />
      <Section>
        <div className="grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            {topicConfig && (
              <div
                className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-sky-50 border border-sky-200"
                data-testid="contact-topic-banner"
              >
                <Sparkles className="w-5 h-5 text-[#0EA5E9] mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-[#0EA5E9] mb-1" data-testid="contact-topic-label">
                    {topicConfig.label}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Tu solicitud será derivada directamente al equipo AVX. Hemos pre-llenado tu mensaje — siéntete libre de editarlo.
                  </p>
                </div>
              </div>
            )}
            <SectionTitle
              title="Solicitud de cotización"
              subtitle="Cuéntanos sobre tu proyecto y necesidades. Respuesta garantizada en 24 horas."
            />
            <div className="p-6 sm:p-8 rounded-2xl bg-white border border-slate-200 shadow-lg">
              <EnterpriseForm
                sourcePage={sourcePage}
                topic={topic}
                prefilledMessage={topicConfig?.message || ''}
              />
            </div>
          </div>
          <div className="lg:col-span-2">
            <SectionTitle title="Otros canales" />
            <div className="space-y-4">
              <a href="https://wa.me/56975294340" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100 hover:bg-emerald-100 transition-colors">
                <MessageCircle className="w-6 h-6 text-emerald-600" />
                <div><div className="text-sm font-semibold text-emerald-700">WhatsApp</div><div className="text-xs text-emerald-600">+56 9 7529 4340</div></div>
              </a>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-sky-50 border border-sky-100">
                <Phone className="w-6 h-6 text-[#0EA5E9]" />
                <div><div className="text-sm font-semibold text-slate-700">Teléfono</div><div className="text-xs text-[#64748B]">+56 9 7529 4340</div></div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-sky-50 border border-sky-100">
                <Mail className="w-6 h-6 text-[#0EA5E9]" />
                <div><div className="text-sm font-semibold text-slate-700">Email</div><div className="text-xs text-[#64748B]">contacto@allcom.cl</div></div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-sky-50 border border-sky-100">
                <MapPin className="w-6 h-6 text-[#0EA5E9]" />
                <div><div className="text-sm font-semibold text-slate-700">Oficina</div><div className="text-xs text-[#64748B]">Andrés Bello 1567 · Providencia · Santiago · Chile</div></div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-sky-50 border border-sky-100">
                <Clock className="w-6 h-6 text-[#0EA5E9]" />
                <div><div className="text-sm font-semibold text-slate-700">Horario comercial</div><div className="text-xs text-[#64748B]">Lun-Vie 9:00 - 18:00 | Soporte 24/7</div></div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
